import { createStyles } from "@mantine/core";

const LINE = "#E5E5E5";
const LINE_SOFT = "#F3F4F6";
const AMBER = "#FFAF4A";
const AMBER_RING = "#FFE0B0";
const AMBER_SOFT = "#FFF6E9";
const MUTED = "#667085";
const MUTED_2 = "#9CA3AF";

const useStyles = createStyles(() => ({
  /* ---------- shell ---------- */
  wrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    // The card owns the rest of the viewport and scrolls inside itself, so the
    // toolbar and breadcrumbs stay put while the listing moves.
    minHeight: "calc(100vh - 300px)",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
    flex: "none",
  },
  search: {
    flex: 1,
    minWidth: 200,
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 38,
    border: "1px solid " + LINE,
    borderRadius: 8,
    padding: "0 12px",
    color: MUTED_2,
    background: "#fff",
    "& input": {
      border: "none",
      outline: "none",
      flex: 1,
      minWidth: 0,
      background: "transparent",
      fontSize: 13.5,
      fontFamily: "Inter",
      color: "#171717",
    },
  },
  btnOutline: {
    height: 38,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid " + LINE,
    background: "#fff",
    fontWeight: 600,
    fontSize: 13,
    fontFamily: "Inter",
    color: "#262626",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover": { background: "#FAFAFA" },
  },
  uploadBtn: {
    height: 38,
    padding: "0 16px",
    borderRadius: 8,
    backgroundColor: AMBER,
    border: "1px solid " + AMBER,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "Inter",
    color: "#171717",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#F09E33" },
  },

  card: {
    background: "#fff",
    border: "1px solid " + LINE,
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderBottom: "1px solid " + LINE_SOFT,
    flex: "none",
  },
  // Scrolls internally so the page itself never gains a scrollbar, in either
  // direction — the list view is the only thing wide enough to need the
  // horizontal one.
  cardBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "auto",
  },

  /* ---------- breadcrumbs ---------- */
  crumbs: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0,
    flexWrap: "wrap",
    fontSize: 13,
    fontFamily: "Inter",
    color: MUTED,
  },
  crumbLink: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontSize: 13,
    fontFamily: "Inter",
    fontWeight: 600,
    color: MUTED,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": { color: "#171717" },
  },
  crumbCurrent: {
    fontSize: 13,
    fontFamily: "Inter",
    fontWeight: 700,
    color: "#171717",
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  crumbSep: {
    display: "inline-flex",
    color: MUTED_2,
    opacity: 0.6,
    flex: "none",
  },

  /* ---------- grid / list toggle ---------- */
  viewToggle: {
    display: "inline-flex",
    background: "#F4F4F5",
    borderRadius: 8,
    padding: 3,
    flex: "none",
  },
  viewToggleBtn: {
    border: "none",
    background: "transparent",
    color: MUTED,
    fontWeight: 600,
    fontSize: 12.5,
    padding: "6px 10px",
    borderRadius: 6,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
  },
  viewToggleBtnOn: {
    background: "#53389E",
    color: "#fff",
  },

  /* ---------- grid ---------- */
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 12,
    padding: 16,
    alignContent: "start",
  },
  section: {
    gridColumn: "1 / -1",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: MUTED,
    fontFamily: "Inter",
    margin: "4px 2px -4px",
    "&:first-of-type": { marginTop: 0 },
  },

  /* folder chip */
  folderChip: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    background: "#F4F4F5",
    border: "1px solid " + LINE,
    borderRadius: 10,
    padding: "10px 6px 10px 10px",
    cursor: "pointer",
    minWidth: 0,
    userSelect: "none",
    "&:hover": { borderColor: "#D0D5DD", background: "#F2F4F7" },
  },
  folderChipIcon: {
    color: AMBER,
    display: "grid",
    placeItems: "center",
    flex: "none",
  },
  folderChipName: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#171717",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
    minWidth: 0,
  },
  folderChipTag: {
    fontSize: 11.5,
    fontFamily: "Inter",
    color: MUTED,
    whiteSpace: "nowrap",
    marginLeft: "auto",
    flex: "none",
  },

  /* file card */
  fileCard: {
    border: "1px solid " + LINE,
    borderRadius: 12,
    padding: "11px 12px 10px",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 9,
    cursor: "pointer",
    userSelect: "none",
    minWidth: 0,
    transition: "box-shadow .12s, transform .12s",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(16,24,40,.14)",
      transform: "translateY(-2px)",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  fileCardHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  fileCardName: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#171717",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileCardMeta: {
    fontSize: 11,
    fontFamily: "Inter",
    color: MUTED,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  // Amber selection ring, shared by chips, cards and list rows.
  selected: {
    borderColor: AMBER + " !important",
    boxShadow: "0 0 0 3px " + AMBER_RING,
    background: AMBER_SOFT,
  },

  /* ---------- preview panel ---------- */
  previewPanel: {
    background: "#F1F3F4",
    borderRadius: 8,
    padding: "10px 14px 0",
    height: 112,
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
  },
  // The white sheet the miniature sits on — top-aligned and cropped, so it
  // reads as the top of a page running off the bottom of the panel.
  previewSheet: {
    width: "100%",
    height: 102,
    alignSelf: "flex-start",
    background: "#fff",
    borderRadius: "3px 3px 0 0",
    boxShadow: "0 1px 3px rgba(0,0,0,.14)",
    overflow: "hidden",
    display: "block",
  },
  previewPage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "top center",
    display: "block",
  },
  // Photos fill the panel edge to edge instead of sitting on a page.
  previewPhoto: {
    padding: 0,
    borderRadius: 6,
    objectFit: "cover",
    objectPosition: "center",
    boxShadow: "none",
  },
  previewGlyph: {
    width: "100%",
    height: 102,
    alignSelf: "flex-start",
    display: "grid",
    placeItems: "center",
  },

  /* ---------- expiry ---------- */
  expiry: {
    color: "#B54708",
    fontWeight: 600,
  },
  expiryPast: {
    color: "#B42318",
    fontWeight: 700,
  },

  /* ---------- list ---------- */
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "10px 12px",
    minWidth: 720,
  },
  listHead: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 12px 8px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    fontFamily: "Inter",
    color: MUTED_2,
    borderBottom: "1px solid " + LINE_SOFT,
    marginBottom: 2,
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 12px",
    borderRadius: 9,
    border: "1px solid transparent",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { background: "#FAFAFA" },
  },
  colName: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: 13.5,
    fontFamily: "Inter",
    color: "#171717",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  colType: {
    width: 100,
    flex: "none",
    fontSize: 12.5,
    fontFamily: "Inter",
    fontWeight: 500,
    color: MUTED,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  colDetails: {
    width: 190,
    flex: "none",
    fontSize: 12.5,
    fontFamily: "Inter",
    fontWeight: 500,
    color: MUTED,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  colExpiry: {
    width: 150,
    flex: "none",
    fontSize: 12.5,
    fontFamily: "Inter",
    fontWeight: 500,
    color: MUTED,
    whiteSpace: "nowrap",
  },
  colMenu: {
    width: 30,
    flex: "none",
    display: "flex",
    justifyContent: "flex-end",
  },
  listFolderIcon: {
    width: 30,
    height: 30,
    flex: "none",
    borderRadius: 9,
    display: "grid",
    placeItems: "center",
    background: AMBER_SOFT,
    color: "#B45309",
  },

  /* ---------- misc ---------- */
  kebab: {
    flex: "none",
    color: MUTED,
  },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "48px 0",
  },
}));

export default useStyles;
